import { useDispatch, useSelector } from 'react-redux';
import { integrationsModel } from '../../../../../../entities/integrations';
import {
  Button,
  Icon,
  Input,
  LoaderDots,
  Tooltip,
} from '../../../../../../shared/ui';
import { useRef, useState } from 'react';
import { useOnClickOutside } from '../../../../../../shared/hooks/useClickOutside';
import {
  CRM_TYPES,
  CRMObject,
  HubspotCompany,
  HubspotContact,
  HubspotObject,
  HubspotObjectTypes,
  SalesforceAccount,
  SalesforceContact,
  SalesforceObject,
  SalesforceOpportunity,
} from '@distribute/shared/types';
import { IconMap } from '../../../../../../shared/sprite';
import { useFormContext } from 'react-hook-form';
import { CreatePageFormSchemaType } from '../../../../config/types';
import cn from 'classnames';
import {
  teamsModel,
  useTeamPermissions,
} from '../../../../../../features/teams';
import { redirectActions } from '../../../../../../entities/history';
import { pageCreationFlowModel } from '../../../../model';
import { SpriteGlyph } from '../../../../../../shared/ui/Icon';

export const CRMSelectInput = () => {
  const dispatch = useDispatch();
  const { watch, setValue } = useFormContext<CreatePageFormSchemaType>();

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { isSalesforceConnected, isHubspotConnected } = useSelector(
    teamsModel.selectors.selectCurrentTeamWithError
  );

  const isCRMObjectsLoading = useSelector(
    integrationsModel.selectors.selectIsCRMObjectsLoading
  );
  const salesforceObjects = useSelector(
    integrationsModel.selectors.selectSalesforceObjects
  );

  const hubspotObjects = useSelector(
    integrationsModel.selectors.selectHubspotObjects
  );

  const { isCanManageAccess } = useTeamPermissions();

  const selectedCRMObject = watch('selectedCRMObject');

  const searchCRMObjects = (search: string) => {
    setSearchValue(search);
    dispatch(integrationsModel.actions.setIsCRMObjectsLoading(true));
    dispatch(integrationsModel.actions.searchCRMObjects({ search }));
  };

  const handleOutsideClick = () => {
    setFocused(false);
  };

  const handleSelect = (object: CRMObject) => {
    setValue('selectedCRMObject', object);
    setFocused(false);
  };

  const handleDelete = () => {
    setValue('selectedCRMObject', null);
  };

  useOnClickOutside(containerRef, handleOutsideClick);

  const handleConnectCRM = () => {
    dispatch(pageCreationFlowModel.actions.closePageCreationFlow());
    dispatch(redirectActions.toTeamIntegrations());
  };

  return (
    <div ref={containerRef} className="relative flex items-end gap-2">
      <div className="relative group grow-1">
        <Input
          type="text"
          value={searchValue}
          placeholder={
            isSalesforceConnected || isHubspotConnected
              ? 'Start typing opportunity, company or contact name...'
              : 'Please connect your CRM first'
          }
          autoComplete="off"
          label="Pull data to and from your CRM"
          onChange={({ target: { value } }) => searchCRMObjects(value)}
          onFocus={() => setFocused(true)}
          heightSize="md"
          disabled={!isSalesforceConnected && !isHubspotConnected}
          ref={inputRef}
        />
        {isCRMObjectsLoading && (
          <LoaderDots size="sm" className="absolute right-4 bottom-4.5" />
        )}

        {!isSalesforceConnected && !isHubspotConnected && (
          <CRMConnectionTooltip
            isCanManageAccess={isCanManageAccess}
            triggerClassNames="absolute right-3 bottom-3"
          >
            <Icon
              glyph={IconMap.HelpCircle}
              width={16}
              className="text-gray-400 cursor-pointer bg-gray-50"
            />
          </CRMConnectionTooltip>
        )}

        {isFocused &&
          (!!salesforceObjects.length ||
            !!hubspotObjects.length ||
            searchValue) &&
          !isCRMObjectsLoading && (
            <ul className="flex flex-col max-w-114 gap-0.5 w-full border border-gray-300 rounded-lg max-h-72 overflow-y-scroll py-0.5 px-1.5 absolute top-17.5 shadow-lg bg-base-white z-50">
              {!salesforceObjects.length && !hubspotObjects.length && (
                <p className="text-sm text-gray-500 pl-2 py-4 font-medium">
                  No results found
                </p>
              )}
              {!!salesforceObjects.length && (
                <>
                  <p className="text-xs text-gray-500 px-2 pt-2 pl-2 uppercase font-bold">
                    Salesforce
                  </p>
                  {salesforceObjects.map((object) => (
                    <CRMObjectRecord
                      key={object.id}
                      obj={object}
                      handleSelect={() => handleSelect(object)}
                      type={CRM_TYPES.SALESFORCE}
                      icon={IconMap.SalesforceBadge}
                    />
                  ))}
                </>
              )}
              {!!hubspotObjects.length && (
                <>
                  <p className="text-xs text-gray-500 px-2 pt-2 pl-2 uppercase font-bold">
                    HubSpot
                  </p>
                  {hubspotObjects.map((object) => (
                    <CRMObjectRecord
                      key={object.id}
                      obj={object}
                      handleSelect={() => handleSelect(object)}
                      type={CRM_TYPES.HUBSPOT}
                      icon={IconMap.HubspotBadge}
                    />
                  ))}
                </>
              )}
            </ul>
          )}

        {selectedCRMObject && (
          <div className="w-full h-10 border border-gray-300 rounded-lg bg-base-white absolute bottom-0 px-2 flex items-center gap-2 overflow-hidden">
            <p className="text-gray-900 truncate text-ellipsis">
              {selectedCRMObject.name}
            </p>
            <p className="text-sm text-gray-500 truncate text-ellipsis">
              {selectedCRMObject.crm === CRM_TYPES.SALESFORCE
                ? getSalesforceSecondaryText(selectedCRMObject)
                : getHubspotSecondaryText(selectedCRMObject)}
            </p>
            <CRMBadge
              obj={selectedCRMObject}
              className="ml-auto"
              icon={
                selectedCRMObject.crm === CRM_TYPES.SALESFORCE
                  ? IconMap.SalesforceBadge
                  : IconMap.HubspotBadge
              }
            />
            <div className="ml-auto absolute right-2 top-1/2 -translate-y-1/2 invisible group-hover:visible">
              <Button
                variant="icon"
                color="link-gray"
                size="xs"
                onClick={() => {
                  handleDelete();
                  inputRef.current?.focus();
                }}
              >
                <Icon glyph={IconMap.XClose} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {!isSalesforceConnected && !isHubspotConnected && (
        <CRMConnectionTooltip
          isCanManageAccess={isCanManageAccess}
          hideTooltip={isCanManageAccess}
        >
          <Button
            variant="text"
            color="secondary"
            size="md"
            disabled={!isCanManageAccess}
            onClick={handleConnectCRM}
          >
            Connect CRM
          </Button>
        </CRMConnectionTooltip>
      )}
    </div>
  );
};

type CRMObjectRecordProps = {
  obj: CRMObject;
  handleSelect: () => void;
  type: CRM_TYPES;
  icon: SpriteGlyph;
};

const CRMObjectRecord = ({
  obj,
  handleSelect,
  type,
  icon,
}: CRMObjectRecordProps) => {
  return (
    <li
      className="flex gap-2 items-start justify-between p-2 hover:bg-base-black-4 cursor-pointer rounded-md"
      onClick={() => handleSelect()}
    >
      <div className="overflow-hidden">
        <p className="font-medium text-gray-900 truncate text-ellipsis mb-0.5">
          {obj.name}
        </p>
        <p className="text-sm text-gray-500 truncate text-ellipsis min-h-5">
          {type === 'salesforce'
            ? getSalesforceSecondaryText(obj as SalesforceObject)
            : getHubspotSecondaryText(obj as HubspotObject)}
        </p>
      </div>
      <CRMBadge obj={obj} icon={icon} />
    </li>
  );
};

type CRMBadgeProps = {
  obj: CRMObject;
  className?: string;
  icon: SpriteGlyph;
};

const CRMBadge = ({ obj, className, icon }: CRMBadgeProps) => {
  return (
    <div
      className={cn(
        'flex flex-shrink-0 flex-basis-auto items-center gap-1.5 px-2 py-1 rounded-full bg-base-black-4',
        className
      )}
    >
      <p className="text-xs text-gray-700 font-medium">{obj.type}</p>
      <Icon glyph={icon} />
    </div>
  );
};

type CRMConnectionTooltipProps = {
  isCanManageAccess: boolean;
  triggerClassNames?: string;
  children: React.ReactElement;
  hideTooltip?: boolean;
};

const CRMConnectionTooltip = ({
  isCanManageAccess,
  triggerClassNames,
  children,
  hideTooltip,
}: CRMConnectionTooltipProps) => {
  if (hideTooltip) {
    return children;
  }

  return (
    <Tooltip
      hideArrow
      sideOffset={4}
      triggerClassNames={triggerClassNames}
      className="px-3 py-3"
      trigger={children}
    >
      <div className="text-xs w-62">
        <p className="font-semibold mb-1">Connect your CRM</p>
        <p className="text-gray-300 font-medium">
          Enable faster page creation and direct updating of data into the CRM{' '}
          <span role="img" aria-label="winking face">
            😉
          </span>{' '}
          <br />
          <span className={cn({ 'text-warning-500': !isCanManageAccess })}>
            {isCanManageAccess
              ? 'Note: This is available only for Pro teams with Salesforce and HubSpot integrations.'
              : 'Ask your team owner or admin to enable CRM integration for your team.'}
          </span>
        </p>
      </div>
    </Tooltip>
  );
};

const getSalesforceSecondaryText = (obj: SalesforceObject) => {
  return obj.type === 'Account'
    ? (obj as SalesforceAccount).website
    : (obj as SalesforceContact | SalesforceOpportunity).email;
};

const getHubspotSecondaryText = (obj: HubspotObject) => {
  return obj.type === HubspotObjectTypes.CONTACT
    ? (obj as HubspotContact).email
    : obj.type === HubspotObjectTypes.COMPANY
    ? (obj as HubspotCompany).domain
    : '';
};
